import { useState, useEffect, useMemo, useContext } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { SelectOptionType, Icon } from '@aidkitorg/component-library';
import { usePost } from '../../../API';
import { useLocalizedStrings } from '../../../Localization';
import { PublicConfigurationContext } from '../../../Context';
import { SupportCaseInfoResponse, MessagesResponse, AssigneesResponse } from '../types';
import { STATUS_OPTIONS } from '../utils';
import { Header } from './Header';
import { LeftSidebar } from './LeftSidebar';
import { ConversationArea } from './ConversationArea';
import { RightSidebar } from './RightSidebar';
import { getAssigneeDisplayName } from './shared/utils';

/**
 * SupportCaseDetailPage - Full-page case detail view without admin panel sidebar
 */
export const SupportCaseDetailPage = () => {
  const { caseId } = useParams<{ caseId: string }>();
  const history = useHistory();
  const queryClient = useQueryClient();
  const L = useLocalizedStrings();
  const publicConfig = useContext(PublicConfigurationContext);
  // Query APIs (for fetching data)
  const fetchSupportCaseInfoAPI = usePost('/support/get_case');
  const fetchMessagesAPI = usePost('/support/get_messages');
  const fetchApplicantMessagesAPI = usePost('/v2/applicant/get_messages');
  const fetchAssigneesAPI = usePost('/support/assignees');
  const fetchRelatedApplicantsAPI = usePost('/support/related_applicants');

  // Mutation APIs (for modifying data)
  const reassignCaseAPI = usePost('/support/reassign_case');
  const resolveCaseAPI = usePost('/support/resolve_case');
  const escalateCaseAPI = usePost('/support/escalate_case');
  const resolveAllMessagesAPI = usePost('/messages/resolve_all');
  const sendMessageAPI = usePost('/applicant/send_message');
  const addToBlocklistAPI = usePost('/admin/add_to_blocklist');

  // Mutations
  const reassignCaseMutation = useMutation({
    mutationFn: (data: { caseId: string; reassignTo: string }) => reassignCaseAPI(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supportCaseInfo', caseId] });
      queryClient.invalidateQueries({ queryKey: ['supportCases'] });
    },
  });

  const resolveCaseMutation = useMutation({
    mutationFn: (data: { caseId: string }) => resolveCaseAPI(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supportCaseInfo', caseId] });
      queryClient.invalidateQueries({ queryKey: ['supportCases'] });
    },
  });

  const escalateCaseMutation = useMutation({
    mutationFn: (data: { caseId: string }) => escalateCaseAPI(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supportCaseInfo', caseId] });
      queryClient.invalidateQueries({ queryKey: ['supportCases'] });
    },
  });

  const resolveAllMessagesMutation = useMutation({
    mutationFn: (data: { applicant?: string; contact?: string; message_ids: number[] }) =>
      resolveAllMessagesAPI(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supportMessages', caseId] });
    },
  });

  const sendMessageMutation = useMutation({
    mutationFn: (data: any) => sendMessageAPI(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supportMessages', caseId] });
    },
  });

  const addToBlocklistMutation = useMutation({
    mutationFn: (data: { contact: string; reason: string }) => addToBlocklistAPI(data),
  });

  // State for dropdowns
  const [status, setStatus] = useState<SelectOptionType | undefined>();
  const [assignedTo, setAssignedTo] = useState<SelectOptionType | undefined>();
  const [showSpinner, setShowSpinner] = useState(false);

  // State for verified contacts
  const [verifiedContacts, setVerifiedContacts] = useState<string[] | null>(null);

  // State for message display options (based on config)
  const showRedactedMessages = !!publicConfig.comms?.showRedactedMessages;
  const showAllContacts = ['Always', 'Checkbox Checked'].includes(publicConfig.comms?.showAllContactComms || '');

  // Fetch the support case info
  const { data: caseInfoResponse, isLoading: isCaseLoading, refetch: refetchCaseInfo } = useQuery({
    queryKey: ['supportCaseInfo', caseId],
    queryFn: async () => {
      const response: SupportCaseInfoResponse = await fetchSupportCaseInfoAPI({
        caseId,
      });
      return response;
    },
    enabled: !!caseId,
  });

  const caseInfo = (caseInfoResponse as SupportCaseInfoResponse | undefined)?.supportCase;

  // Fetch assignees (moved before useEffect to avoid hoisting issues)
  const { data: assigneesData } = useQuery<AssigneesResponse>({
    queryKey: ['supportAssignees'],
    queryFn: async (): Promise<AssigneesResponse> => {
      const response = await fetchAssigneesAPI({});
      return response as AssigneesResponse;
    },
  });

  // Convert assignees to select options with user icon
  const assigneeOptions = useMemo(() => {
    const assignees = assigneesData?.assignees || [];
    const options = assignees.map(assignee => ({
      label: (
        <div className="flex items-center gap-2">
          <Icon role="decorative" type="solid" name="UserIcon" className="w-4 h-4 text-gray-500" />
          <span>{getAssigneeDisplayName(assignee)}</span>
        </div>
      ),
      value: assignee.uid,
    }));

    // Add "Unassigned" option at the beginning
    return [
      {
        label: L.support.unassigned,
        value: 'unassigned',
      },
      ...options
    ];
  }, [assigneesData, L.support]);

  // Set initial state values when case info loads
  useEffect(() => {
    if (caseInfo) {
      // Set status from case info - find matching status from STATUS_OPTIONS
      if (caseInfo.status) {
        const matchingStatus = STATUS_OPTIONS.find(opt => opt.value === caseInfo.status);
        if (matchingStatus) {
          setStatus(matchingStatus);
        }
      }

      // Set assigned to from case info - find matching assignee from assigneeOptions
      if (assigneeOptions.length > 0) {
        // Check if case is assigned to someone or unassigned ('system' means unassigned)
        if (caseInfo.assigned_to && caseInfo.assigned_to !== 'system') {
          const matchingAssignee = assigneeOptions.find(opt => opt.value === caseInfo.assigned_to);
          if (matchingAssignee) {
            setAssignedTo(matchingAssignee);
          }
        } else {
          // Case is unassigned - select the "Unassigned" option
          const unassignedOption = assigneeOptions.find(opt => opt.value === 'unassigned');
          if (unassignedOption) {
            setAssignedTo(unassignedOption);
          }
        }
      }

      // Parse case history to extract verified contacts
      if (caseInfo.history && typeof caseInfo.history === 'object' && !Array.isArray(caseInfo.history)) {
        const verified = Object.entries(caseInfo.history).reduce<string[]>((agg, [_created_at, obj]: [string, any]) => {
          if (obj.action === 'verify_finished' && obj.metadata?.contact && !agg.includes(obj.metadata.contact)) {
            agg.push(obj.metadata.contact);
          }
          return agg;
        }, []);
        setVerifiedContacts(verified);
      }
    }
  }, [caseInfo, assigneeOptions]);

  // Fetch related applicants and cases FIRST (before messages query)
  const { data: relatedData, isLoading: isRelatedDataLoading } = useQuery({
    queryKey: ['relatedApplicants', caseId, caseInfo?.channel],
    queryFn: async () => {
      const response = await fetchRelatedApplicantsAPI({
        caseId,
        channel: caseInfo?.channel || 'default',
      });
      return response;
    },
    enabled: !!caseId,
  });

  // Get applicant UID from related data (before messages query)
  const applicantUid = useMemo(() => {
    if (relatedData?.applicantRecords) {
      const uids = Object.keys(relatedData.applicantRecords);
      if (uids.length > 0) {
        return uids[0];
      }
    }
    return undefined;
  }, [relatedData?.applicantRecords]);

  // Fetch messages for this case's contact (AFTER applicantUid is available)
  const { data: messagesData, isLoading: isMessagesLoading, error: messagesError } = useQuery<MessagesResponse>({
    queryKey: ['supportMessages', caseId, caseInfo?.contact, applicantUid, showRedactedMessages, showAllContacts],
    queryFn: async (): Promise<MessagesResponse> => {
      // If we have an ingested applicant (UID <= 22 chars), use the applicant messages endpoint
      if (applicantUid && applicantUid.length <= 22) {
        const response = await fetchApplicantMessagesAPI({
          channel: caseInfo?.channel || 'default',
          applicant: applicantUid,
          contactNameKey: 'legal_name', // default to legal_name (matches old support page)
          showRedactedMessages: showRedactedMessages, // Show messages from redacted applicants
          allContacts: showAllContacts, // Include messages from alternate contacts
          sortOrder: 'asc' // Show oldest messages first in conversation view
        });
        return response as MessagesResponse;
      } else {
        // Otherwise use the standard contact-based endpoint
        const response = await fetchMessagesAPI({
          contact: caseInfo?.contact,
          channel: caseInfo?.channel || 'default',
          sortOrder: 'asc' // Show oldest messages first in conversation view
        });
        return response as MessagesResponse;
      }
    },
    enabled: !!caseInfo?.contact,
    refetchInterval: 5000, // Auto-refresh every 5 seconds
  });

  // Filter status options based on current status (matching old page logic)
  const filteredStatusOptions = useMemo(() => {
    const currentStatus = caseInfo?.status;

    // If case is resolved or closed, only show current status (dropdown will be disabled)
    if (currentStatus === 'resolved' || currentStatus === 'closed') {
      return STATUS_OPTIONS.filter(opt => opt.value === currentStatus);
    }

    // For other statuses, show current status + available actions
    return STATUS_OPTIONS.filter(opt => {
      if (opt.value === currentStatus) return true;
      if (opt.value === 'resolved') return true;
      if (opt.value === 'escalated') return currentStatus !== 'escalated';
      return false;
    });
  }, [caseInfo?.status]);

  // Get applicant info from related data
  const applicantInfo = useMemo(() => {
    if (relatedData?.applicantRecords) {
      const uids = Object.keys(relatedData.applicantRecords);
      // If there's at least one applicant, return the first one's info
      if (uids.length > 0) {
        return relatedData.applicantRecords[uids[0]];
      }
    }
    return undefined;
  }, [relatedData?.applicantRecords]);

  const isLoading = isCaseLoading || isMessagesLoading;

  // Delay showing the spinner to avoid flashing for fast loads
  useEffect(() => {
    if (isLoading) {
      const timer = setTimeout(() => {
        setShowSpinner(true);
      }, 300); // Show spinner after 300ms
      return () => clearTimeout(timer);
    } else {
      setShowSpinner(false);
    }
  }, [isLoading]);

  if (isLoading && !caseInfo) {
    return showSpinner ? (
      <div className="flex items-center justify-center h-screen w-full">
        <div className="text-gray-500">{L.support.loading}</div>
      </div>
    ) : null;
  }

  if (!caseInfo) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-500">{L.support.support_case_does_not_exist}</div>
      </div>
    );
  }

  const messages = messagesData?.data || [];
  const unhandledCount = messagesData?.unhandled_count || 0;

  const handleBack = () => {
    history.push('/admin-panel/support');
  };

  const resolveAllUnhandledMessages = async (messageIds: number[]) => {
    await resolveAllMessagesAPI({
      applicant: applicantUid || 'unknown',
      message_ids: messageIds
    });
    // Refetch messages to update the UI
    queryClient.invalidateQueries({ queryKey: ['supportMessages', caseId, caseInfo?.contact] });
  };

  const handleResolveAllUnhandled = async () => {
    // Get all unhandled message IDs
    const unhandledMessageIds = messagesData?.data
      ?.filter(m => m.needs_attention)
      .map(m => m.id as number) || [];

    if (unhandledMessageIds.length > 0) {
      await resolveAllUnhandledMessages(unhandledMessageIds);
    }
  };

  const handleSendMessage = async (
    message: string,
    method: 'email' | 'sms' | 'whatsapp',
    sendAt?: Date,
    contactKeys?: { nameKey: string; phoneKey: string; emailKey: string },
    contentTemplate?: any
  ) => {
    // Use provided contactKeys or default to legal_name
    const nameKey = contactKeys?.nameKey || 'legal_name';

    await sendMessageMutation.mutateAsync({
      channel: caseInfo?.channel || 'default',
      method,
      contact: applicantUid
        ? { applicant: applicantUid, withNameKey: nameKey }
        : { supportCase: caseInfo.uid, applicant: applicantUid, withNameKey: nameKey },
      message,
      ...(sendAt && { sendAt }),
      ...(contentTemplate && { contentTemplate }),
      lang: applicantInfo?.['language'] || 'en'
    });
  };

  const handleStatusChange = async (newStatus: SelectOptionType) => {
    try {
      // Handle resolve action
      if (newStatus.value === 'resolved') {
        // Check if there are unhandled messages
        if (messagesData?.data && messagesData.data.some((m) => m.needs_attention)) {
          const doHandle = window.confirm(L.support.mark_unhandled);
          if (doHandle) {
            const unhandledMessageIds = messagesData.data
              .filter((m) => m.needs_attention)
              .map((m) => m.id as number);
            await resolveAllUnhandledMessages(unhandledMessageIds);
          }
        }

        const yn = window.confirm(L.support.confirm_mark_resolved);
        if (!yn) return;

        const caseUpdate = await resolveCaseMutation.mutateAsync({ caseId: caseId || '' });
        if (caseUpdate?.row) {
          // Update local state
          setStatus(newStatus);
          await refetchCaseInfo();
        }
      }
      // Handle escalate action
      else if (newStatus.value === 'escalated') {
        const yn = window.confirm(L.support.confirm_escalate_case);
        if (!yn) return;

        const caseUpdate = await escalateCaseMutation.mutateAsync({ caseId: caseId || '' });
        if (caseUpdate?.row) {
          // Update local state
          setStatus(newStatus);
          await refetchCaseInfo();
        }
      }
      // For other status changes, just update the state (no API call needed yet)
      else {
        setStatus(newStatus);
      }
    } catch (error) {
      console.error('Error updating case status:', error);
      // Optionally show error toast to user
    }
  };

  const handleAssignedToChange = async (newAssignee: SelectOptionType) => {
    try {
      // If "Unassigned" is selected, send 'system' to the API
      const assigneeValue = newAssignee.value === 'unassigned' ? 'system' : newAssignee.value;

      await reassignCaseMutation.mutateAsync({
        caseId: caseId || '',
        reassignTo: assigneeValue,
      });
      setAssignedTo(newAssignee);
    } catch (error) {
      console.error('Error reassigning case:', error);
      // Optionally show error toast to user
    }
  };

  const handleReportSpam = async (reason: string) => {
    try {
      // 1. Add contact to blocklist
      await addToBlocklistMutation.mutateAsync({
        contact: caseInfo?.contact || '',
        reason: reason || ''
      });

      // 2. Resolve all unhandled messages if any exist
      if (messagesData?.data && messagesData.data.some((m) => m.needs_attention)) {
        const unhandledMessageIds = messagesData.data
          .filter((m) => m.needs_attention)
          .map((m) => m.id as number);

        await resolveAllMessagesMutation.mutateAsync({
          ...(applicantUid ? { applicant: applicantUid } : { contact: caseInfo?.contact }),
          message_ids: unhandledMessageIds
        });
      }

      // 3. Resolve the case
      await resolveCaseMutation.mutateAsync({ caseId: caseId || '' });

      // 4. Refetch case info
      await refetchCaseInfo();
    } catch (error) {
      console.error('Error reporting spam:', error);
      throw error;
    }
  };

  const handleResolveParentCase = async () => {
    // Check if there are unhandled messages
    if (messagesData?.data && messagesData.data.some((m) => m.needs_attention)) {
      const doHandle = window.confirm(L.support.mark_unhandled);
      if (doHandle) {
        const unhandledMessageIds = messagesData.data
          .filter((m) => m.needs_attention)
          .map((m) => m.id as number);
        await resolveAllUnhandledMessages(unhandledMessageIds);
      }
    }

    const yn = window.confirm(L.support.confirm_mark_resolved);
    if (!yn) return;

    await resolveCaseMutation.mutateAsync({ caseId: caseId || '' });
    await refetchCaseInfo();
  };

  return (
    <div className="h-screen flex flex-col bg-gray-50 w-full">
      {/* Header */}
      <Header
        caseInfo={caseInfo}
        onBack={handleBack}
        status={status}
        onStatusChange={handleStatusChange}
        assignedTo={assignedTo}
        onAssignedToChange={handleAssignedToChange}
        assigneeOptions={assigneeOptions}
        assignees={assigneesData?.assignees || []}
        statusOptions={filteredStatusOptions}
        updatingStatus={resolveCaseMutation.isPending || escalateCaseMutation.isPending}
        updatingAssignee={reassignCaseMutation.isPending}
        onReportSpam={handleReportSpam}
        messages={messages}
      />

      {/* Main Content Area - 3 column layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar */}
        <div className="w-72 bg-white border-r overflow-y-auto overflow-x-hidden">
          <LeftSidebar
            caseInfo={caseInfo}
            relatedCases={relatedData?.unresolvedCases || []}
            applicantInfo={applicantInfo}
            applicantUid={applicantUid}
            verifiedContacts={verifiedContacts}
          />
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <ConversationArea
            caseInfo={caseInfo}
            messages={messages}
            unhandledCount={unhandledCount}
            onResolveAllUnhandled={handleResolveAllUnhandled}
            onSendMessage={handleSendMessage}
            applicantInfo={{ ...applicantInfo, uid: applicantUid }}
            isLoading={isMessagesLoading || isRelatedDataLoading}
            error={messagesError as Error | null}
          />
        </div>

        {/* Right Sidebar */}
        <div className="w-72 bg-white border-l overflow-y-auto">
          <RightSidebar
            caseInfo={caseInfo}
            applicantUid={applicantUid}
            verifiedContacts={verifiedContacts}
            applicantInfo={applicantInfo}
            channel={caseInfo?.channel || undefined}
            onResolveParentCase={handleResolveParentCase}
          />
        </div>
      </div>
    </div>
  );
};
